import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchPortalConfig } from '@/shared/services/portal.service'
import { applyBrandTheme, resetBrandTheme } from '@/shared/composables/useBrandTheme'
import { setFavicon, setBaseDocumentTitle } from '@/shared/utils/documentMeta'
import { PORTAL_BRANDING_DEFAULTS } from '@/shared/constants/portalBranding'
import type { PortalConfigData, PortalCustomizations, PortalFiles } from '@/shared/types/portal'
import type { User } from '@/modules/auth/types'
import { useAuthStore } from '@/modules/auth'

interface SessionBranding {
  label?: string
  legal_name?: string
  customizations?: PortalCustomizations | null
  files?: PortalFiles | null
}

const trimUrl = (url?: string | null) => url?.trim() || null

/** White-label portal config (domain) + session overrides (logged-in user reseller). */
export const usePortalBrandingStore = defineStore('portalBranding', () => {
  const portalConfig = ref<PortalConfigData | null>(null)
  const sessionBranding = ref<SessionBranding | null>(null)
  const isLoading = ref(false)
  const isReady = ref(false)

  // Check if logged in user has no portal domain
  const userHasNoPortalDomain = computed(() => {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated || !authStore.user) {
      return false
    }
    return !authStore.user.reseller_portal_domain?.trim()
  })

  // Merge domain customization defaults with active session overrides
  const effectiveCustomizations = computed((): PortalCustomizations => {
    if (userHasNoPortalDomain.value) {
      return {}
    }
    return {
      ...(portalConfig.value?.portal_customizations ?? {}),
      ...(sessionBranding.value?.customizations ?? {})
    }
  })

  // Merge domain files with active session files dynamically
  const effectiveFiles = computed((): PortalFiles => {
    if (userHasNoPortalDomain.value) {
      return {}
    }
    const base = portalConfig.value?.portal_files ?? {}
    const override = sessionBranding.value?.files ?? {}
    const merged = { ...base }
    for (const [key, value] of Object.entries(override)) {
      if (value?.trim()) {
        merged[key as keyof PortalFiles] = value
      }
    }
    return merged
  })

  // Application display label (e.g. "Qvara" or reseller custom label)
  const appLabel = computed(() => {
    if (userHasNoPortalDomain.value) {
      return PORTAL_BRANDING_DEFAULTS.appLabel
    }
    return (
      sessionBranding.value?.label ?? portalConfig.value?.label ?? PORTAL_BRANDING_DEFAULTS.appLabel
    )
  })

  // Application legal/copyright name (e.g. "PrimeVOX" or reseller legal name)
  const appLegalLabel = computed(() => {
    if (userHasNoPortalDomain.value) {
      return PORTAL_BRANDING_DEFAULTS.copyrightName || appLabel.value
    }
    return sessionBranding.value?.legal_name ?? portalConfig.value?.legal_name ?? appLabel.value
  })

  // Theme primary color used for guest/login pages
  const guestHex = computed(() => effectiveCustomizations.value.guest_hex?.trim() || null)

  // Theme primary color used for buttons
  const buttonHex = computed(() => effectiveCustomizations.value.button_hex?.trim() || null)

  // Custom background color for the sidebar
  const sidebarHex = computed(() => effectiveCustomizations.value.sidebar_hex?.trim() || null)

  // Custom text color for the sidebar elements
  const sidebarTextHex = computed(
    () => effectiveCustomizations.value.sidebar_text_hex?.trim() || null
  )

  // Header text shown on the login/guest layout
  const guestHeader = computed(
    () => effectiveCustomizations.value.guest_header || PORTAL_BRANDING_DEFAULTS.guestHeader
  )

  // Subtitle/description text shown on the login/guest layout
  const guestText = computed(
    () => effectiveCustomizations.value.guest_text || PORTAL_BRANDING_DEFAULTS.guestText
  )

  // Text label for call-to-action button on the login/guest layout
  const guestCtaText = computed(() => effectiveCustomizations.value.guest_cta_text || null)

  // Target URL for call-to-action button on the login/guest layout
  const guestUrl = computed(() => effectiveCustomizations.value.guest_url || null)

  // Helper checking if a login call-to-action is fully configured
  const hasGuestCta = computed(() => !!(guestCtaText.value && guestUrl.value))

  // Footer links (e.g. Privacy, Terms) filtered for valid text and href
  const footerLinks = computed(
    () =>
      effectiveCustomizations.value.footer_links?.filter((l) => l.text?.trim() && l.href?.trim()) ??
      []
  )

  // Resolved legal name
  const legalName = computed(
    () => effectiveCustomizations.value.legal_name || PORTAL_BRANDING_DEFAULTS.legalName
  )
  // Resolved copyright line text
  const copyrightLine = computed(
    () => `© ${new Date().getFullYear()} ${legalName.value}. All rights reserved.`
  )

  // Asset URLs (with automatic fallbacks)

  // URL of the full color logo
  const logoColorUrl = computed(
    () =>
      trimUrl(effectiveFiles.value.logo_color) ||
      trimUrl(effectiveFiles.value.logo_white) ||
      PORTAL_BRANDING_DEFAULTS.logoColor
  )

  // URL of the white version of the logo
  const logoWhiteUrl = computed(
    () =>
      trimUrl(effectiveFiles.value.logo_white) ||
      trimUrl(effectiveFiles.value.logo_color) ||
      PORTAL_BRANDING_DEFAULTS.logoWhite
  )

  // URL of the white icon-only version of the logo
  const logoIconWhiteUrl = computed(
    () =>
      trimUrl(effectiveFiles.value.icon_white) ||
      trimUrl(effectiveFiles.value.logo_white) ||
      trimUrl(effectiveFiles.value.logo_color) ||
      PORTAL_BRANDING_DEFAULTS.logoIconWhite
  )

  // URL of the favicon
  const faviconUrl = computed(
    () =>
      trimUrl(effectiveFiles.value.favicon) ||
      trimUrl(effectiveFiles.value.web_app_icon) ||
      PORTAL_BRANDING_DEFAULTS.favicon
  )

  // URL of the background image for guest/login pages
  const guestBackgroundUrl = computed(() => {
    if (userHasNoPortalDomain.value) {
      return PORTAL_BRANDING_DEFAULTS.guestBackground
    }
    const customBg = trimUrl(effectiveFiles.value.guest_background)
    if (customBg) return customBg

    const hasResellerContext = !!(portalConfig.value?.reseller_id || sessionBranding.value)
    if (!hasResellerContext) {
      return PORTAL_BRANDING_DEFAULTS.guestBackground
    }

    return null
  })

  // URL of the main artwork/image for guest/login pages
  const guestImageUrl = computed(() => {
    if (userHasNoPortalDomain.value) {
      return null
    }
    return trimUrl(effectiveFiles.value.guest_image)
  })

  // Synchronization helpers

  // Apply resolved colors, favicon, and base title to DOM
  function syncAll(): void {
    const gHex = guestHex.value || buttonHex.value
    const bHex = buttonHex.value || guestHex.value
    if (gHex || bHex) {
      applyBrandTheme({
        guestHex: gHex || PORTAL_BRANDING_DEFAULTS.guestHex,
        buttonHex: bHex || PORTAL_BRANDING_DEFAULTS.buttonHex,
        sidebarHex: sidebarHex.value,
        sidebarTextHex: sidebarTextHex.value
      })
    } else {
      resetBrandTheme()
    }

    const favicon = faviconUrl.value
    setFavicon(favicon === PORTAL_BRANDING_DEFAULTS.favicon ? null : favicon)
    setBaseDocumentTitle(appLabel.value)
  }

  // Load portal configuration from server for the current domain
  async function loadPortalConfig(): Promise<void> {
    const response = await fetchPortalConfig()
    if (response.result === 'success' && response.data) {
      portalConfig.value = response.data
    }
  }

  /** Apply authenticated user's reseller branding over domain portal config. */
  function applyFromUser(user: User | null): void {
    sessionBranding.value = user?.reseller_id
      ? {
          label: user.reseller_name ?? undefined,
          legal_name: user.reseller_legal_name ?? undefined,
          customizations: user.reseller_customizations ?? null,
          files: user.reseller_files ?? null
        }
      : null
    syncAll()
  }

  /** Boot sequence: portal-config for everyone; refresh profile branding when logged in. */
  async function initialize(options: {
    isAuthenticated: boolean
    user: User | null
    fetchProfile?: () => Promise<unknown>
  }): Promise<void> {
    isLoading.value = true
    try {
      try {
        await loadPortalConfig()
      } catch (err) {
        console.warn('Portal config lookup failed, using default branding', err)
      }
      sessionBranding.value = null
      syncAll()

      if (options.isAuthenticated) {
        if (options.fetchProfile) {
          await options
            .fetchProfile()
            .catch((err) => console.warn('Profile refresh failed during branding init', err))
        }
        if (options.user) {
          applyFromUser(options.user)
        }
      }
    } catch (error) {
      console.error('Portal branding initialization failed completely', error)
      syncAll()
    } finally {
      isLoading.value = false
      isReady.value = true
    }
  }

  /** After logout: drop session branding and restore domain portal config. */
  async function onLogout(): Promise<void> {
    sessionBranding.value = null
    await loadPortalConfig().catch((err) =>
      console.warn('Failed to reload portal config after logout', err)
    )
    syncAll()
  }

  return {
    portalConfig,
    sessionBranding,
    isLoading,
    isReady,
    appLabel,
    appLegalLabel,
    guestHex,
    buttonHex,
    guestHeader,
    guestText,
    guestCtaText,
    guestUrl,
    footerLinks,
    logoColorUrl,
    logoWhiteUrl,
    logoIconWhiteUrl,
    faviconUrl,
    guestBackgroundUrl,
    guestImageUrl,
    hasGuestCta,
    copyrightLine,
    loadPortalConfig,
    applyFromUser,
    initialize,
    onLogout,
    syncAll
  }
})
